import Cookies from "js-cookie";
import { getEnv } from "@/env";
import {
  MARKETPLACE_ATTRIBUTION_COOKIE,
  MARKETPLACE_ATTRIBUTION_COOKIE_PATH,
  MARKETPLACE_ATTRIBUTION_SESSION_KEY,
} from "@/lib/marketplace-attribution/constants";
import { DefaultCookieSettings } from "@/lib/ecommerce/commercetools/client-only/constants";
import {
  getMarketplaceAttributionParams,
  hasMarketplaceAttributionParams,
  resolveMarketplaceAttributionParams,
} from "@/utils/functions/marketplaceAttribution";

/**
 * js-cookie's default writer un-escapes `=` and `&`, which would store the same
 * attribution in a different shape than the middleware and the bootstrap
 * snippet, both of which write `encodeURIComponent(params.toString())`.
 */
const attributionCookies = Cookies.withConverter({
  write: (value: string) => encodeURIComponent(value),
});

const getCookieDomain = (): string | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }

  if (window.location.hostname === "localhost") {
    return undefined;
  }

  return getEnv("COOKIE_DOMAIN", ".edx.org");
};

export function serializeMarketplaceAttribution(
  source: URLSearchParams | string,
): string {
  return getMarketplaceAttributionParams(source).toString();
}

export function readStoredMarketplaceAttribution(): URLSearchParams {
  if (typeof window === "undefined") {
    return new URLSearchParams();
  }

  const cookieValue = attributionCookies.get(MARKETPLACE_ATTRIBUTION_COOKIE);
  if (cookieValue) {
    return getMarketplaceAttributionParams(cookieValue);
  }

  try {
    const sessionValue = sessionStorage.getItem(
      MARKETPLACE_ATTRIBUTION_SESSION_KEY,
    );
    if (sessionValue) {
      return getMarketplaceAttributionParams(sessionValue);
    }
  } catch {
    // sessionStorage may be unavailable (private mode / blocked storage)
  }

  return new URLSearchParams();
}

export function persistMarketplaceAttribution(
  source: URLSearchParams | string,
): void {
  if (typeof window === "undefined") {
    return;
  }

  const serialized = serializeMarketplaceAttribution(source);
  if (!serialized) {
    return;
  }

  const domain = getCookieDomain();
  attributionCookies.set(MARKETPLACE_ATTRIBUTION_COOKIE, serialized, {
    path: MARKETPLACE_ATTRIBUTION_COOKIE_PATH,
    ...DefaultCookieSettings,
    ...(domain ? { domain } : {}),
  });

  try {
    sessionStorage.setItem(MARKETPLACE_ATTRIBUTION_SESSION_KEY, serialized);
  } catch {
    // sessionStorage may be unavailable
  }
}

/**
 * Read-only. Writing here would run during render for every attributed link on
 * the page; persistence belongs to the middleware, the bootstrap snippet, and
 * `MarketplaceAttributionPersistence`.
 */
export function resolveClientMarketplaceAttribution(
  primary: URLSearchParams | string,
): URLSearchParams {
  if (hasMarketplaceAttributionParams(primary)) {
    return getMarketplaceAttributionParams(primary);
  }

  return resolveMarketplaceAttributionParams(
    primary,
    readStoredMarketplaceAttribution(),
  );
}
